import { AlertColor } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { ErrorResponse } from '../api/hscan';
import {
  Case,
  deleteCase,
  DicomDownloadEvent,
  FilterContent,
  generateHpacsLinkId,
  generateShareCode,
  HpacsLinkResponse,
  Report,
  RequestCaseByIdsQueryParams,
  requestCaseByIds,
  CdDeliveryRequestBody,
  requestPay,
  verifyPayment,
  requestCase,
  requestDicomImage,
  requestDownloadImage,
  requestLinkByCode,
  requestReport,
  ShareCode,
  uploadDicoms,
  UploadDicomsBody,
  uploadImages,
  UploadImagesBody,
  UploadProgress,
} from '../api/hscan/case';
import { CdDeliveryPayment } from '../api/hscan/hospital';
import { Page } from '../api/page';
import { getFilenameFromResponseHeader } from '../api/utils';
import {
  DOWNLOAD_STATUS,
  UPLOAD_STATUS,
  useProgressStackContext,
} from '../context/ProgressStackContext';
import { HPACS_URL, HSCAN_BACKEND_API_URL } from '../env';

import { useUserState } from './accountHook';
import { MutationOption, QueryOption } from './react-query-type';

export interface CaseParams {
  filter?: FilterContent;
  size?: number;
}

export interface CaseParamsWithPage extends CaseParams {
  page?: number;
}
export const useCase = (
  params: CaseParamsWithPage,
  options?: QueryOption<Page<Case>>,
) => {
  const [user] = useUserState();
  const fetch = useCallback(async () => {
    const { data } = await requestCase(params);
    return data;
  }, [params]);

  return useQuery({
    queryKey: ['requestCase', params, user?.userId],
    queryFn: fetch,
    // TODO: getNextPageParam은 원래 useInfiniteQuery의 옵션.
    //  제대로 기능하지 않고 있었을 것. 확인 후 제거 필요.
    // getNextPageParam: lastPage =>
    //   lastPage ? getNextPage(lastPage) : undefined,
    ...options,
  });
};

export const useCaseByIds = (
  params: RequestCaseByIdsQueryParams,
  options?: QueryOption<Case[]>,
) => {
  const [user] = useUserState();
  const fetch = useCallback(async () => {
    const { data } = await requestCaseByIds(params);
    return data;
  }, [params]);

  return useQuery({
    queryKey: ['cases', params, user?.userId],
    queryFn: fetch,
    ...options,
  });
};

export const useDeleteCase = (
  options?: MutationOption<string[], unknown, AxiosError<ErrorResponse>>,
) => {
  const client = useQueryClient();
  const fetch = useCallback(async (caseIds: string[]) => {
    await deleteCase({ caseIds });
  }, []);
  return useMutation({
    mutationFn: fetch,
    ...options,
    onSuccess: (...params) => {
      client.invalidateQueries({ queryKey: ['requestCase'] });
      options?.onSuccess && options.onSuccess(...params);
    },
  });
};

export interface LinkParams {
  caseId: string[];
  startDtime?: string;
  endDtime?: string;
}
export const useLink = (
  options?: MutationOption<
    LinkParams,
    HpacsLinkResponse,
    AxiosError<ErrorResponse>
  >,
) => {
  const fetch = useCallback(async (params: LinkParams) => {
    const { caseId, startDtime, endDtime } = params;
    const now = startDtime ?? new Date().toISOString();
    const expire =
      endDtime ?? new Date(new Date().getTime() + 86400000).toISOString();
    const { data } = await generateHpacsLinkId({
      caseId,
      startDtime: now,
      endDtime: expire,
    });
    return data;
  }, []);
  return useMutation({ mutationFn: fetch, ...options });
};

export const useGetLinkUrl = () => {
  const { mutateAsync: requestLink } = useLink({
    onError(error) {
      console.warn(error.response?.status, error.response?.data);
    },
  });

  return useCallback(
    async (caseIds: string[]) => {
      const link = await requestLink({ caseId: caseIds });
      return `${HPACS_URL}/linkLogin/${link?.id}`;
    },
    [requestLink],
  );
};

export const useShareCode = (
  options?: MutationOption<LinkParams, ShareCode>,
) => {
  const fetch = useCallback(async (params: LinkParams) => {
    const { caseId, startDtime, endDtime } = params;
    const { data } = await generateShareCode({
      caseId,
      startDtime,
      endDtime,
    });
    return data;
  }, []);
  return useMutation({ mutationFn: fetch, ...options });
};

export const useLinkByCode = (
  code: string,
  options?: QueryOption<
    HpacsLinkResponse,
    HpacsLinkResponse,
    AxiosError<ErrorResponse<ErrorResponse>>
  >,
) => {
  const fetch = async () => {
    const { data } = await requestLinkByCode({ code });
    return data;
  };
  return useQuery({
    queryKey: ['link-by-code', code],
    queryFn: fetch,
    ...options,
  });
};

export interface UploadProgressEventHandler {
  onStart: () => void;
  onProgress: (percentage: number) => void;
  onComplete: () => void;
  onError: (message: string) => void;
}
export const useUploadTrayProgressNotifier = (
  id: string,
): UploadProgressEventHandler => {
  const { addStack, updateStack } = useProgressStackContext();
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.uploadImages.modal',
  });

  return useMemo(
    () => ({
      onStart: () =>
        addStack?.({
          id,
          type: 'UPLOAD',
          message: `${t('uploading')}`,
          status: UPLOAD_STATUS.STARTED,
        }),
      onProgress: (percentage: number) =>
        updateStack?.({
          id,
          type: 'UPLOAD',
          message: `${t('uploading')}`,
          progress: { percentage },
          status: UPLOAD_STATUS.IN_PROGRESS,
        }),
      onComplete: () =>
        updateStack?.({
          id,
          type: 'UPLOAD',
          message: `${t('success')}`,
          status: UPLOAD_STATUS.COMPLETED,
        }),
      onError: (message: string) =>
        updateStack?.({
          id,
          type: 'UPLOAD',
          message,
          status: UPLOAD_STATUS.FAILED,
        }),
    }),
    [addStack, id, t, updateStack],
  );
};

export interface FormParams {
  form: UploadImagesBody;
  files: File[];
}
export const useForm = (
  options?: MutationOption<FormParams, Case[]>,
  onProgress?: (p: UploadProgress) => void,
) => {
  const fetch = useCallback(
    async (params: FormParams) => {
      const { form, files } = params;
      if (!form.patientId) {
        form.patientId = uuidv4();
      }
      const { data } = await uploadImages(form, files, onProgress);
      return data;
    },
    [onProgress],
  );
  return useMutation({ mutationFn: fetch, ...options });
};

export interface DicomParams {
  patient: UploadDicomsBody;
  files: File[];
}
export const useDicom = (
  options?: MutationOption<DicomParams, Case[]>,
  onProgress?: (p: UploadProgress) => void,
) => {
  const fetch = useCallback(
    async (params: DicomParams) => {
      const { patient, files } = params;
      const { data } = await uploadDicoms(patient, files, onProgress);
      return data;
    },
    [onProgress],
  );
  return useMutation({ mutationFn: fetch, ...options });
};

export const useDicomImage = (imageId: string) => {
  const fetch = useCallback(async () => {
    const { data: file } = await requestDicomImage(imageId);
    return file;
  }, [imageId]);
  return useQuery({
    queryKey: ['requestDicomImage', imageId],
    queryFn: fetch,
    staleTime: 1000 * 3600 * 24,
  });
};

export interface DownloadOptions {
  onDownloadStart?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
}
export const useDownloadFile = (
  fetchData: () => Promise<AxiosResponse<File> | undefined>,
  options?: DownloadOptions,
) => {
  const { onDownloadStart, onSuccess, onError: _onError } = options ?? {};

  const onClickDownload = () => {
    onDownloadStart?.();
    fetchData().then(
      value => {
        if (value) {
          let filename = '';
          if (value?.headers['content-disposition']) {
            filename = getFilenameFromResponseHeader(value);
          }
          saveFile(value.data, filename);
          onSuccess?.();
        }
      },
      () => {
        _onError?.();
      },
    );
  };
  return onClickDownload;
};

function saveFile(source: File, filename: string) {
  if (!source) {
    return;
  }
  const anchor = document.createElement('a') as HTMLAnchorElement;
  const fileUrl = window.URL.createObjectURL(source);
  anchor.href = fileUrl;
  if (filename) {
    anchor.download = filename;
  }
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(fileUrl);
}

export interface DownloadProgress {
  /** image progress */
  progress: number;
  /** 케이스 당 총 이미지 수 */
  totalImageCount: number;
  /** 현재 케이스 */
  currentCase: number;
  /** 총 케이스 수 */
  totalCaseCount: number;
}
export interface DownloadProgressEventHandler {
  /**
   * 다운로드 시작
   */
  onStart: () => void;
  /**
   * 파일 다운 중
   * @param downloadProgress 진행 상황
   */
  onProgress: (downloadProgress: DownloadProgress) => void;
  /**
   * 파일 압축 중
   */
  onCompress: () => void;
  /**
   * 압축 파일 다운 중
   */
  onDownload: () => void;
  /**
   * 다운로드 완료
   */
  onComplete: () => void;
}
export const useSnackbarDownloadNotifier = (
  handleOpen: (params: {
    message: string;
    severity?: AlertColor | undefined;
    autoHide?: boolean | undefined;
    hideDuration?: number | undefined;
  }) => void,
): DownloadProgressEventHandler => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.imageList.downloadButton',
  });
  return {
    onStart: () => handleOpen({ message: t('message.start') }),
    onProgress: (downloadProgress: DownloadProgress) =>
      handleOpen({
        message: t('message.downloading', {
          case: downloadProgress.totalCaseCount,
          currentCase: downloadProgress.currentCase,
          progress: downloadProgress.progress,
          total: downloadProgress.totalImageCount,
        }),
        autoHide: false,
      }),
    onCompress: () =>
      handleOpen({ message: t('message.compress'), autoHide: false }),
    onDownload: () =>
      handleOpen({ message: t('message.downloadCompressed'), autoHide: false }),
    onComplete: () =>
      handleOpen({ message: t('message.success'), severity: 'success' }),
  };
};
export const useProgressTrayDownloadNotifier = (
  id: string,
): DownloadProgressEventHandler => {
  const { addStack, updateStack } = useProgressStackContext();
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.imageList.downloadButton',
  });
  return {
    onStart: () =>
      addStack?.({
        id,
        type: 'DOWNLOAD',
        message: `${t('message.start')}`,
        status: DOWNLOAD_STATUS.STARTED,
      }),
    onProgress: (downloadProgress: DownloadProgress) =>
      updateStack?.({
        id,
        progress: {
          progress: downloadProgress.progress,
          totalImageCount: downloadProgress.totalImageCount,
          currentCase: downloadProgress.currentCase,
          totalCaseCount: downloadProgress.totalCaseCount,
        },
        message: `${t('message.downloading', {
          case: downloadProgress.progress,
          currentCase: downloadProgress.totalImageCount,
          progress: downloadProgress.currentCase,
          total: downloadProgress.totalCaseCount,
        })}`,
        status: DOWNLOAD_STATUS.IN_PROGRESS,
      }),
    onCompress: () =>
      updateStack?.({
        id,
        message: `${t('message.compress')}`,
        status: DOWNLOAD_STATUS.COMPRESS,
      }),
    onDownload: () =>
      updateStack?.({
        id,
        message: `${t('message.downloadCompressed')}`,
      }),
    onComplete: () =>
      updateStack?.({
        id,
        message: `${t('message.success')}`,
        status: DOWNLOAD_STATUS.COMPLETED,
      }),
  };
};
export const useSubscribeDownloadProgress = (
  sseKey: string,
  downloadProgressEventHandler: DownloadProgressEventHandler,
) => {
  const subscribeDownloadProgress = () => {
    const eventSource = new EventSourcePolyfill(
      `${HSCAN_BACKEND_API_URL}/case/dicom/sse/${sseKey}/event`,
      {
        heartbeatTimeout: 60 * 60 * 1000,
        withCredentials: true,
      },
    );

    let progress = 0;
    let currentCase = 1;
    eventSource.onmessage = ev => {
      progress = progress + 1;
      const eventData = JSON.parse(ev.data) as DicomDownloadEvent;
      if (currentCase && currentCase !== eventData.completeCount) {
        currentCase = currentCase + 1;
        progress = 0;
      }
      if (eventData.eventId === 'DOWNLOAD') {
        downloadProgressEventHandler.onProgress({
          progress,
          totalImageCount: eventData.totalImageCount,
          currentCase,
          totalCaseCount: eventData.totalCount,
        });
      } else if (eventData.eventId === 'COMPRESS') {
        downloadProgressEventHandler.onCompress();
      }
      if (
        eventData.eventId === 'COMPLETE' ||
        (eventData.eventId === 'COMPRESS' && // COMPLETE가 오지 않을 때를 방지하기 위한 로직
          eventData.completeCount === eventData.totalCount &&
          progress === eventData.totalImageCount)
      ) {
        downloadProgressEventHandler.onDownload();
        eventSource.close();
      }
    };

    eventSource.onerror = _ => {
      eventSource.close();
      progress = 0;
      currentCase = 1;
    };
  };

  return { subscribeDownloadProgress };
};

export const useDownloadImage = (
  caseIds: string[],
  sseKey: string | undefined,
  options?: DownloadOptions,
) => {
  const fetchData = useCallback(async () => {
    if (sseKey) {
      return requestDownloadImage(sseKey, caseIds);
    }
  }, [caseIds, sseKey]);
  return useDownloadFile(fetchData, options);
};

export const useReport = (caseId: string, options?: QueryOption<Report>) => {
  const fetch = async () => {
    const { data } = await requestReport(caseId);
    return data;
  };
  return useQuery({
    queryKey: ['report', caseId],
    queryFn: fetch,
    ...options,
  });
};

export const useRequestCdDelivery = (
  options?: MutationOption<
    CdDeliveryRequestBody,
    CdDeliveryPayment,
    AxiosError<ErrorResponse>
  >,
) => {
  const fetch = useCallback(async (body: CdDeliveryRequestBody) => {
    const { data } = await requestPay(body);
    return data;
  }, []);

  return useMutation({ mutationFn: fetch, ...options });
};

export const useVerifyCdDeliveryPayment = (
  options?: MutationOption<string, void, AxiosError<ErrorResponse>>,
) => {
  const fetch = useCallback(async (paymentId: string) => {
    await verifyPayment({ paymentId });
  }, []);

  return useMutation({ mutationFn: fetch, ...options });
};
